import { Injectable, DestroyRef, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, debounceTime, first, map, of, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from '../i18n.service';
import { compressToUTF16, decompressFromUTF16 } from 'lz-string';

@Injectable({
  providedIn: 'root',
})
export class TranslateService {
  requests: {
    text: string;
    callback: (translated: string) => void;
  }[] = [];
  targetLanguage = 'de';
  storageKey = 'compressed_translation_map';
  cache: Map<string, string> = new Map();
  waitobj = new Subject<any>();
  destroyRef = inject(DestroyRef);
  constructor(private http: HttpClient, private i18nService: I18nService) {}

  init() {
    this.observeLanguageChange();
    this.waitobj
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((i: any) => {
          if (i?.text?.length > 0) {
            this.requests.push(i);
          }
        }),
        debounceTime(300)
      )
      .subscribe((searchValue) => {
        this.triggerTranslation(this.targetLanguage);
      });
  }

  async observeLanguageChange() {
    this.i18nService.langOb
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang) => {
        this.targetLanguage = lang;
        this.triggerTranslation(lang);
        this.waitobj.next(null);
      });
  }

  collect(text: string, callback: (translated: string) => void) {
    this.waitobj.next({
      text,
      callback,
    });
  }

  triggerTranslation(targetLanguage) {
    this.targetLanguage = targetLanguage;
    this.cache = this.getCacheAndDecompress();
    let newTexts = this.requests
      .filter((r) => !this.cache.has(r.text))
      .map((r) => r.text);
    if (this.cache && this.cache.size) {
      newTexts = this.extractNotCached(newTexts) || [];
    }
    if (newTexts.length > 0) {
      this.translate({ texts: newTexts, targetLanguage })
        .pipe(first())
        .subscribe((res: Map<string, string>) => {
          try {
            this.updateCache(res);
            this.compressAndStoreCache();
            this.doCallBacks();
          } catch (err) {
            console.log(err);
          }
        });
    } else {
      this.doCallBacks();
    }
  }

  getCacheAndDecompress(): Map<string, string> {
    let compressed: string = localStorage.getItem(
      this.storageKey + this.targetLanguage
    );
    if (compressed) {
      const decompressed = decompressFromUTF16(compressed);
      if (decompressed?.length > 0) {
        const data = JSON.parse(decompressed);
        return new Map<string, string>(Object.entries(data));
      }
    }
    return new Map();
  }

  extractNotCached(newTexts: string[]): string[] {
    if (!this.cache || !this.cache.size) return newTexts;
    let filtered: string[] = [];
    newTexts.forEach((text) => {
      if (!this.cache.has(text)) {
        filtered.push(text);
      }
    });
    return filtered;
  }

  updateCache(res) {
    try {
      for (let key of Object.keys(res)) {
        this.cache.set(key, res[key]);
      }
    } catch (err) {
      console.log(err);
    }
  }

  compressAndStoreCache() {
    const plainObject = Object.fromEntries(this.cache);
    const original = JSON.stringify(plainObject);
    const compressed = compressToUTF16(original);
    localStorage.setItem(this.storageKey + this.targetLanguage, compressed);
  }

  translate(body) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'ngsw-bypass': '',
      }),
    };
    return this.http
      .post('api/multilang/translate', { body }, httpOptions)
      .pipe(catchError((res: Map<string, string>) => of(res)));
  }

  doCallBacks() {
    for (let request of this.requests) {
      let key = request.text;
      if (this.cache.has(key)) {
        request.callback(this.cache.get(key));
      }
    }
  }
}
